import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { FaWhatsapp } from 'react-icons/fa';
import { getTranslation } from '../../utils/translations';
import { STRIPE_CONFIG, getApiUrl } from '../../config/constants';
import './ReservationModal.css';

// Load Stripe with configuration from constants
const stripePromise = loadStripe(STRIPE_CONFIG.PUBLIC_KEY);

const ReservationForm = ({ parcelData, projectData, language, onClose, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [userInfo, setUserInfo] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  // Get user info from localStorage on component mount
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setUserInfo(user);
      } catch (error) {
        setPaymentError(language === "ar" ? "خطأ في بيانات المستخدم" : "Error in user data");
      }
    } else {
      setPaymentError(language === "ar" ? "يجب تسجيل الدخول أولاً" : "Please login first");
    }
  }, [language]);

  const formatPrice = (price) => {
    if (!price) return "0";
    return new Intl.NumberFormat( "en-US").format(price);
  };

  const handleWhatsAppClick = () => {
    // Use WhatsApp URL from project data, fallback to default if not available
    const whatsappUrl = projectData?.whatsapp_url || parcelData?.project?.whatsapp_url || "https://wa.me/message/JG2HFCFKRV6OK1";
    window.open(whatsappUrl, '_blank');
  };



  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements || !userInfo) {
      return;
    }

    setIsProcessing(true);
    setPaymentError(null);

    try {
      const token = localStorage.getItem('token');

      // Create reservation on backend with authenticated user
      const reservationResponse = await fetch(getApiUrl('/reservations'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          parcel_id: parcelData.id,
          // User info will be taken from authenticated user on backend
        })
      });
      
      if (!reservationResponse.ok) {
        throw new Error('Failed to create reservation');
      }
      
      const reservationData = await reservationResponse.json();
      
      // Confirm payment with Stripe
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        reservationData.data.client_secret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: userInfo.name,
              email: userInfo.email,
              phone: userInfo.phone,
            },
          }
        }
      );
      
      if (error) {
        setPaymentError(error.message);
        console.error('Payment error:', error);
      } else if (paymentIntent.status === 'succeeded') {
        // Payment successful
        
        // Confirm payment on backend
        const token = localStorage.getItem('token');
        await fetch(getApiUrl('/payments/confirm'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            reservation_id: reservationData.data.id,
            payment_intent_id: paymentIntent.id
          })
        });
        
        onSuccess(reservationData.data);
      }
    } catch (error) {
      console.error('Reservation error:', error);
      setPaymentError(language === "ar" ? "حدث خطأ أثناء معالجة الدفع" : "An error occurred while processing payment");
    } finally {
      setIsProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
    hidePostalCode: true,
  };

  return (
    <div className="reservation-form" dir={language === "ar" ? "rtl" : "ltr"}>
      {/* Header */}
      <div className="modal-header">
        <h2>{getTranslation("reserveUnit", language)}</h2>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>

      {/* Parcel Information */}
      <div className="parcel-info">
        <h3>{getTranslation("unitDetails", language)}</h3>
        <div className="info-grid">
          <div className="info-item">
            <span className="label">{getTranslation("unitNumber", language)}</span>
            <span className="value">{parcelData.parcel_number}</span>
          </div>
          <div className="info-item">
            <span className="label">{getTranslation("areaLabel", language)}</span>
            <span className="value">{formatPrice(parcelData.area)} {getTranslation("sqm", language)}</span>
          </div>
          <div className="info-item">
            <span className="label">{getTranslation("priceLabel", language)}</span>
            <span className="value">{formatPrice(parcelData.total_price || parcelData.price_with_tax || 200000)} {getTranslation("sar", language)}</span>
          </div>
          {parcelData.expected_roi && (
            <div className="info-item">
              <span className="label">{getTranslation("expectedRoi", language)}</span>
              <span className="value roi-value">{parcelData.expected_roi.toString().includes('%') ? parcelData.expected_roi : `${parcelData.expected_roi}%`}</span>
            </div>
          )}
          <div className="info-item">
            <span className="label">{getTranslation("reservationAmount", language)}</span>
            <span className="value">{formatPrice(parcelData.effective_reservation_amount || parcelData.reservation_amount || 
                       (parcelData.project?.reservation_amount) || 50000)} {getTranslation("sar", language)}</span>
          </div>
        </div>
      </div>

      {/* Show error if user is not logged in */}
      {!userInfo && paymentError && (
        <div className="error-message">
          {paymentError}
        </div>
      )}

      {/* Payment Form */}
      {userInfo && (
        <form onSubmit={handlePaymentSubmit} className="payment-form">
          <h3>{getTranslation("paymentInfo", language)}</h3>

          <div className="form-group">
            <label>{getTranslation("cardDetails", language)}</label>
            <div className="card-element-container">
              <CardElement options={cardElementOptions} />
            </div>
          </div>

          <div className="payment-summary">
            <div className="total-amount">
              <span>{getTranslation("totalAmount", language)}</span>
              <span className="amount">{formatPrice(parcelData.effective_reservation_amount || parcelData.reservation_amount || 
                       (parcelData.project?.reservation_amount) || 50000)} {getTranslation("sar", language)}</span>
            </div>
          </div>

          {paymentError && (
            <div className="error-message">
              {paymentError}
            </div>
          )}

          <div className="form-actions">
            <div className="payment-buttons">
              <button
                type="submit"
                className="pay-btn"
                disabled={!stripe || isProcessing}
              >
                {isProcessing ?
                  (language === "ar" ? "جار المعالجة..." : "Processing...") :
                  `${getTranslation("payNow", language)} ${formatPrice(parcelData.effective_reservation_amount || parcelData.reservation_amount ||
                         (parcelData.project?.reservation_amount) || 50000)} ${getTranslation("sar", language)}`
                }
              </button>
              <button
                type="button"
                className="whatsapp-btn"
                onClick={handleWhatsAppClick}
                disabled={isProcessing}
              >
                <FaWhatsapp className="whatsapp-icon" />
                {getTranslation("sales", language)}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

const ReservationModal = ({ parcelData, projectData, language, isOpen, onClose, onSuccess }) => {
  if (!isOpen || !parcelData) return null;

  return (
    <div className="reservation-modal-overlay" onClick={onClose}>
      <div className="reservation-modal" onClick={(e) => e.stopPropagation()}>
        <Elements stripe={stripePromise}>
          <ReservationForm
            parcelData={parcelData}
            projectData={projectData}
            language={language}
            onClose={onClose}
            onSuccess={onSuccess}
          />
        </Elements>
      </div>
    </div>
  );
};

export default ReservationModal; 